import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import type { EmployeeOrgpath } from './useAssignPositionQuery';

export interface AreaTreeNode {
    area_id: number;
    area_name: string;
    area_level: number;
    subcompany_id: number;
    subareas: AreaTreeNode[];
    work_positions: WorkPositionTreeNode[];
}

export interface WorkPositionTreeNode {
    work_position_id: number;
    work_position_name: string;
}

export interface PositionRow {
    local_id: string;
    hiring_orgpath_id?: number | null;
    to_delete: boolean;
    subcompany_id: number;
    subcompany_name: string;
    area_ids: number[];
    area_path_names: string[];
    work_position_id: number;
    work_position_name: string;
    start_date: Dayjs | null;
    end_date: Dayjs | null;
    editing: boolean;
}

interface Props {
    hirings: HiringRange[];
    subcompanies: Subcompany[];
    areas: AreaTreeNode[];
    orgpaths?: EmployeeOrgpath[];
    employee_id?: number;
    employee_name?: string;
}

const genLocalId = () => `pr-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const parseDateSafe = (value?: string | null): Dayjs | null => {
    if (!value) return null;
    const parsed = dayjs(value);
    if (parsed.isValid()) return parsed;

    const ddmmyyyy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
    if (!ddmmyyyy) return null;

    const [, d, m, y] = ddmmyyyy;
    const normalized = dayjs(`${y}-${m}-${d}`);
    return normalized.isValid() ? normalized : null;
};

const getOrgpathRange = (op: EmployeeOrgpath): { start_date?: string; end_date?: string } | null => {
    const anyOp = op as any;
    return op.Range ?? anyOp.range ?? null;
};

const getOrgpathAreas = (op: EmployeeOrgpath): Array<{ id?: number | string; area_id?: number; name?: string; area_name?: string; lvl?: number; area_level?: number }> => {
    const anyOp = op as any;
    return (op.Area ?? anyOp.area ?? anyOp.areas ?? []) as Array<{
        id?: number | string;
        area_id?: number;
        name?: string;
        area_name?: string;
        lvl?: number;
        area_level?: number;
    }>;
};

const getOrgpathSubcompany = (op: EmployeeOrgpath): { id?: number | string; subcompany_id?: number; name?: string; subcompany_name?: string } | null => {
    const anyOp = op as any;
    return (op.Subcompany ?? anyOp.subcompany ?? anyOp.SubCompany ?? null) as { id?: number | string; subcompany_id?: number; name?: string; subcompany_name?: string } | null;
};

const getOrgpathPosition = (op: EmployeeOrgpath): { id?: number | string; work_position_id?: number; name?: string; work_position_name?: string } | null => {
    const anyOp = op as any;
    return (op.Posicion ?? anyOp.position ?? anyOp.Position ?? anyOp.Puesto ?? null) as { id?: number | string; work_position_id?: number; name?: string; work_position_name?: string } | null;
};

const rangesOverlap = (aStart: Dayjs | null, aEnd: Dayjs | null, bStart: Dayjs | null, bEnd: Dayjs | null): boolean => {
    if (!aStart?.isValid() || !aEnd?.isValid() || !bStart?.isValid() || !bEnd?.isValid()) return false;
    return !aEnd.isBefore(bStart, 'day') && !aStart.isAfter(bEnd, 'day');
};

const computeDateErrors = (rows: PositionRow[], contractStart: Dayjs | null, contractEnd: Dayjs | null): Record<string, string[]> => {
    if (!contractStart?.isValid() || !contractEnd?.isValid()) return {};

    const map: Record<string, string[]> = {};
    const add = (id: string, msg: string) => {
        (map[id] ??= []).push(msg);
    };
    const dated = rows.filter((r) => !r.to_delete && r.start_date?.isValid() && r.end_date?.isValid());

    // Validación 1: fechas fuera del rango del contrato
    for (const row of dated) {
        if (row.start_date!.isBefore(contractStart, 'day')) {
            add(row.local_id, `Inicio (${row.start_date!.format('DD/MM/YYYY')}) anterior al contrato (${contractStart.format('DD/MM/YYYY')})`);
        }
        if (row.end_date!.isAfter(contractEnd, 'day')) {
            add(row.local_id, `Fin (${row.end_date!.format('DD/MM/YYYY')}) posterior al contrato (${contractEnd.format('DD/MM/YYYY')})`);
        }
    }

    // Validación 2: solapamiento entre asignaciones
    for (let i = 0; i < dated.length; i++) {
        for (let j = i + 1; j < dated.length; j++) {
            const a = dated[i];
            const b = dated[j];
            if (rangesOverlap(a.start_date!, a.end_date!, b.start_date!, b.end_date!)) {
                add(a.local_id, `Fechas solapadas con "${b.work_position_name}"`);
                add(b.local_id, `Fechas solapadas con "${a.work_position_name}"`);
            }
        }
    }

    // Validación 3: continuidad (sin gaps)
    const sorted = [...dated].sort((a, b) => a.start_date!.diff(b.start_date!, 'day'));
    for (let i = 1; i < sorted.length; i++) {
        const prev = sorted[i - 1];
        const curr = sorted[i];
        const expected = prev.end_date!.add(1, 'day');
        if (!curr.start_date!.isSame(expected, 'day')) {
            add(curr.local_id, `Inicio no continuo: se esperaba ${expected.format('DD/MM/YYYY')} ("${prev.work_position_name}" finaliza ${prev.end_date!.format('DD/MM/YYYY')})`);
        }
    }

    return map;
};

const findPositionPath = (
    nodes: AreaTreeNode[],
    workPositionId: number,
    ancestorIds: number[] = [],
    ancestorNames: string[] = [],
    // subcompany_id se propaga desde el nodo raíz; los subnodos pueden tenerlo vacío en el API
    inheritedSubcompanyId: number = 0,
): { area_ids: number[]; area_path_names: string[]; subcompany_id: number } | null => {
    for (const area of nodes) {
        const nextIds = [...ancestorIds, area.area_id];
        const nextNames = [...ancestorNames, area.area_name];
        const effectiveSubcompanyId = area.subcompany_id || inheritedSubcompanyId;

        if (area.work_positions?.some((wp) => wp.work_position_id === workPositionId)) {
            return {
                area_ids: nextIds,
                area_path_names: nextNames,
                subcompany_id: effectiveSubcompanyId,
            };
        }

        if (area.subareas?.length) {
            const found = findPositionPath(area.subareas, workPositionId, nextIds, nextNames, effectiveSubcompanyId);
            if (found) return found;
        }
    }

    return null;
};

const getOrgpathHiringId = (op: EmployeeOrgpath): number | null => {
    const anyOp = op as any;
    const raw = op.hiring_id ?? op.Hiring?.hiring_id ?? op.hiring?.hiring_id ?? anyOp.hiringId ?? anyOp.hiring?.id;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

// Resuelve hiring_range_id → hiring_id cuando el API no devuelve hiring_id directamente.
const resolveViaHiringRangeId = (op: EmployeeOrgpath, hirings: HiringRange[]): number | null => {
    const anyOp = op as any;
    const rawRangeId = Number(anyOp.hiring_range_id ?? anyOp.hiringRangeId ?? 0);
    if (!rawRangeId) return null;
    const match = hirings.find((h) => h.hiring_range_id === rawRangeId);
    return match?.hiring?.hiring_id ?? null;
};

const resolveOrgpathHiringId = (op: EmployeeOrgpath, hirings: HiringRange[]): number | null => {
    const direct = getOrgpathHiringId(op);
    if (direct) return direct;

    const viaRange = resolveViaHiringRangeId(op, hirings);
    if (viaRange) return viaRange;

    const range = getOrgpathRange(op);
    const start = parseDateSafe(range?.start_date);
    const end = parseDateSafe(range?.end_date);
    if (!start?.isValid() || !end?.isValid()) return null;

    const exact = hirings.find((h) => {
        const hStart = parseDateSafe(h.start_date);
        const hEnd = parseDateSafe(h.end_date);
        return hStart?.isValid() && hEnd?.isValid() && hStart.isSame(start, 'day') && hEnd.isSame(end, 'day');
    });
    if (exact?.hiring?.hiring_id) return exact.hiring.hiring_id;

    const overlap = hirings.find((h) => {
        const hStart = parseDateSafe(h.start_date);
        const hEnd = parseDateSafe(h.end_date);
        return rangesOverlap(hStart, hEnd, start, end);
    });
    return overlap?.hiring?.hiring_id ?? null;
};

const buildRowFromOrgpath = (op: EmployeeOrgpath, areas: AreaTreeNode[], subcompanies: Subcompany[]): PositionRow | null => {
    const position = getOrgpathPosition(op);
    const workPositionId = position?.work_position_id ?? Number(position?.id ?? 0);
    if (!workPositionId) return null;

    const subcompanyNode = getOrgpathSubcompany(op);
    const rawSubcompanyId = subcompanyNode?.subcompany_id ?? Number(subcompanyNode?.id ?? 0);
    const path = findPositionPath(areas, workPositionId);

    let subcompany_id = rawSubcompanyId || 0;
    let area_ids: number[] = [];
    let area_path_names: string[] = [];

    if (path) {
        area_ids = path.area_ids;
        area_path_names = path.area_path_names;
        subcompany_id = subcompany_id || path.subcompany_id;
    } else {
        const areaNodes = getOrgpathAreas(op);
        if (areaNodes.length) {
            const sorted = [...areaNodes].sort((a, b) => (a.lvl ?? a.area_level ?? 0) - (b.lvl ?? b.area_level ?? 0));
            area_ids = sorted.map((a) => Number(a.area_id ?? a.id ?? 0)).filter((n) => !!n);
            area_path_names = sorted.map((a) => a.area_name ?? a.name ?? '');
        }
    }

    const subcompany = subcompanies.find((s) => s.subcompany_id === subcompany_id);
    const range = getOrgpathRange(op);
    const today = dayjs().startOf('day');
    const parsedStart = parseDateSafe(range?.start_date);
    const parsedEnd = parseDateSafe(range?.end_date);
    const hiring_orgpath_id = op.hiring_orpath_id as number;
    const finalRow: PositionRow = {
        local_id: genLocalId(),
        hiring_orgpath_id: hiring_orgpath_id,
        to_delete: false,
        subcompany_id,
        subcompany_name: subcompany?.subcompany_name ?? subcompanyNode?.subcompany_name ?? subcompanyNode?.name ?? '',
        area_ids,
        area_path_names,
        work_position_id: workPositionId,
        work_position_name: position?.work_position_name ?? position?.name ?? '',
        start_date: parsedStart?.isValid() ? parsedStart : today,
        end_date: parsedEnd?.isValid() ? parsedEnd : today,
        editing: false,
    };

    return finalRow;
};

export const useAssignPositionForm = ({ hirings, subcompanies, areas, orgpaths, employee_id, employee_name }: Props) => {
    const [selectedHiringId, setSelectedHiringId] = useState<number | null>(null);
    const [rows, setRows] = useState<PositionRow[]>([]);
    const [dirty, setDirty] = useState(false);

    const selectedHiring = useMemo(() => hirings.find((h) => h.hiring?.hiring_id === selectedHiringId) ?? null, [hirings, selectedHiringId]);

    const subcompanyTree = useMemo(
        () =>
            subcompanies.map((sc) => ({
                subcompany: sc,
                rootAreas: areas.filter((a) => a.subcompany_id === sc.subcompany_id && a.area_level === 1),
            })),
        [subcompanies, areas],
    );

    const preloadedByHiring = useMemo(() => {
        const map: Record<number, PositionRow[]> = {};

        // Filtrar solo root areas para que findPositionPath construya el path completo
        const rootAreas = areas.filter((a) => a.area_level === 1);
        for (const op of orgpaths ?? []) {
            const hiringId = resolveOrgpathHiringId(op, hirings);
            if (!hiringId) {
                continue;
            }

            const row = buildRowFromOrgpath(op, rootAreas, subcompanies);
            if (!row) {
                continue;
            }

            // Recorta las fechas al rango del contrato (el DB puede guardar 3000-01-01 como fecha abierta)
            const hiring = hirings.find((h) => h.hiring?.hiring_id === hiringId);
            const cStart = parseDateSafe(hiring?.start_date);
            const cEnd = parseDateSafe(hiring?.end_date);
            const clampedRow: PositionRow = {
                ...row,
                start_date: row.start_date && cStart?.isValid() && row.start_date.isBefore(cStart, 'day') ? cStart : row.start_date,
                end_date: row.end_date && cEnd?.isValid() && row.end_date.isAfter(cEnd, 'day') ? cEnd : row.end_date,
            };

            (map[hiringId] ??= []).push(clampedRow);
        }

        return map;
    }, [orgpaths, hirings, areas, subcompanies]);

    const preloadedCountByHiring = useMemo(() => {
        const counts: Record<number, number> = {};
        for (const key of Object.keys(preloadedByHiring)) {
            counts[Number(key)] = preloadedByHiring[Number(key)].length;
        }
        return counts;
    }, [preloadedByHiring]);

    const preloadedTotalContracts = useMemo(() => Object.keys(preloadedByHiring).length, [preloadedByHiring]);
    const preloadedTotalPositions = useMemo(() => Object.values(preloadedByHiring).reduce((acc, list) => acc + list.length, 0), [preloadedByHiring]);

    const getRowsForHiring = useCallback(
        (hiringId: number): PositionRow[] => {
            const direct = preloadedByHiring[hiringId] ?? [];
            if (direct.length > 0) return direct;

            const selectedContract = hirings.find((h) => h.hiring?.hiring_id === hiringId);
            if (!selectedContract) return [];

            const contractStart = parseDateSafe(selectedContract.start_date);
            const contractEnd = parseDateSafe(selectedContract.end_date);
            if (!contractStart?.isValid() || !contractEnd?.isValid()) return [];

            const rootAreas = areas.filter((a) => a.area_level === 1);
            const fallbackRows = (orgpaths ?? [])
                .map((op) => {
                    const row = buildRowFromOrgpath(op, rootAreas, subcompanies);
                    if (!row) return null;

                    const opHiringId = getOrgpathHiringId(op);
                    if (opHiringId && opHiringId === hiringId) return row;

                    const range = getOrgpathRange(op);
                    const opStart = parseDateSafe(range?.start_date);
                    const opEnd = parseDateSafe(range?.end_date);
                    return rangesOverlap(contractStart, contractEnd, opStart, opEnd) ? row : null;
                })
                .filter((row): row is PositionRow => !!row);

            const dedup = new Map<string, PositionRow>();
            for (const row of fallbackRows) {
                const key = `${row.work_position_id}-${row.subcompany_id}-${row.area_ids.join('.')}-${row.start_date?.format('YYYY-MM-DD')}-${row.end_date?.format('YYYY-MM-DD')}`;
                if (!dedup.has(key)) dedup.set(key, row);
            }
            return Array.from(dedup.values());
        },
        [preloadedByHiring, hirings, orgpaths, areas, subcompanies],
    );

    useEffect(() => {
        if (selectedHiringId == null) {
            setRows([]);
            return;
        }
        setRows(getRowsForHiring(selectedHiringId));
    }, [selectedHiringId, getRowsForHiring]);

    const selectHiring = useCallback(
        (next: number | null) => {
            const nextRows = next == null ? [] : getRowsForHiring(next);
            setSelectedHiringId(next);
            setDirty(false);
            if (next == null) {
                setRows([]);
                return;
            }
            setRows(nextRows);
        },
        [getRowsForHiring],
    );

    const reset = useCallback(() => {
        setSelectedHiringId(null);
        setRows([]);
        setDirty(false);
    }, []);

    const addRowFromTree = useCallback(
        (params: { workPositionId: number; workPositionName: string }): { added: boolean; reason?: 'duplicate' | 'not_found' } => {
            const path = findPositionPath(
                areas.filter((a) => a.area_level === 1),
                params.workPositionId,
            );
            if (!path) return { added: false, reason: 'not_found' };

            const subcompany = subcompanies.find((sc) => sc.subcompany_id === path.subcompany_id);
            let added = true;

            setRows((prev) => {
                if (prev.some((r) => r.work_position_id === params.workPositionId && !r.to_delete)) {
                    added = false;
                    return prev;
                }

                const today = dayjs().startOf('day');

                // start_date sigue al fin del último puesto activo; fallback a hoy si no hay filas previas
                let defaultStart: Dayjs = today;
                if (prev.length > 0) {
                    const validEnds = prev.filter((r) => !r.to_delete && r.end_date?.isValid()).map((r) => r.end_date!);
                    if (validEnds.length > 0) {
                        const maxEnd = validEnds.reduce((m, d) => (d.isAfter(m) ? d : m));
                        defaultStart = maxEnd.add(1, 'day');
                    }
                }

                const newRow: PositionRow = {
                    local_id: genLocalId(),
                    hiring_orgpath_id: null,
                    to_delete: false,
                    subcompany_id: path.subcompany_id,
                    subcompany_name: subcompany?.subcompany_name ?? '',
                    area_ids: path.area_ids,
                    area_path_names: path.area_path_names,
                    work_position_id: params.workPositionId,
                    work_position_name: params.workPositionName,
                    start_date: defaultStart,
                    end_date: defaultStart.add(1, 'day'),
                    editing: true,
                };

                return [...prev, newRow];
            });

            if (added) setDirty(true);
            return added ? { added: true } : { added: false, reason: 'duplicate' };
        },
        [areas, subcompanies],
    );

    const removeRow = useCallback((localId: string) => {
        setRows((prev) => {
            const row = prev.find((r) => r.local_id === localId);
            if (!row) return prev;
            // Nuevos (sin ID en BD) se descartan; existentes se marcan para borrado en backend
            if (!row.hiring_orgpath_id) return prev.filter((r) => r.local_id !== localId);
            return prev.map((r) => (r.local_id === localId ? { ...r, to_delete: true } : r));
        });
        setDirty(true);
    }, []);

    const restoreRow = useCallback((localId: string) => {
        setRows((prev) => prev.map((r) => (r.local_id === localId ? { ...r, to_delete: false } : r)));
        setDirty(true);
    }, []);

    const toggleEditRow = useCallback((localId: string) => {
        setRows((prev) => prev.map((r) => (r.local_id === localId ? { ...r, editing: !r.editing } : r)));
    }, []);

    const updateRow = useCallback((localId: string, patch: Partial<PositionRow>) => {
        setRows((prev) => prev.map((r) => (r.local_id === localId ? { ...r, ...patch } : r)));
        setDirty(true);
    }, []);

    const changeRowSubcompany = useCallback(
        (localId: string, subcompanyId: number) => {
            const subcompany = subcompanies.find((sc) => sc.subcompany_id === subcompanyId);
            setRows((prev) =>
                prev.map((r) =>
                    r.local_id === localId
                        ? {
                              ...r,
                              subcompany_id: subcompanyId,
                              subcompany_name: subcompany?.subcompany_name ?? '',
                              area_ids: [],
                              area_path_names: [],
                              work_position_id: 0,
                              work_position_name: '',
                          }
                        : r,
                ),
            );
            setDirty(true);
        },
        [subcompanies],
    );

    const changeRowArea = useCallback(
        (localId: string, area: AreaTreeNode | null) => {
            setRows((prev) =>
                prev.map((r) => {
                    if (r.local_id !== localId) return r;
                    if (!area) {
                        return { ...r, area_ids: [], area_path_names: [], work_position_id: 0, work_position_name: '' };
                    }

                    const findAreaPath = (nodes: AreaTreeNode[], targetId: number, idAcc: number[] = [], nameAcc: string[] = []): { ids: number[]; names: string[] } | null => {
                        for (const node of nodes) {
                            const nextIds = [...idAcc, node.area_id];
                            const nextNames = [...nameAcc, node.area_name];
                            if (node.area_id === targetId) return { ids: nextIds, names: nextNames };
                            if (node.subareas?.length) {
                                const f = findAreaPath(node.subareas, targetId, nextIds, nextNames);
                                if (f) return f;
                            }
                        }
                        return null;
                    };

                    const roots = areas.filter((a) => a.subcompany_id === r.subcompany_id && a.area_level === 1);
                    const found = findAreaPath(roots, area.area_id) ?? { ids: [area.area_id], names: [area.area_name] };
                    return {
                        ...r,
                        area_ids: found.ids,
                        area_path_names: found.names,
                        work_position_id: 0,
                        work_position_name: '',
                    };
                }),
            );
            setDirty(true);
        },
        [areas],
    );

    const changeRowWorkPosition = useCallback((localId: string, wp: WorkPositionTreeNode | null) => {
        setRows((prev) =>
            prev.map((r) =>
                r.local_id === localId
                    ? {
                          ...r,
                          work_position_id: wp?.work_position_id ?? 0,
                          work_position_name: wp?.work_position_name ?? '',
                      }
                    : r,
            ),
        );
        setDirty(true);
    }, []);

    const changeRowDate = useCallback((localId: string, field: 'start_date' | 'end_date', value: Dayjs | null) => {
        setRows((prev) => prev.map((r) => (r.local_id === localId ? { ...r, [field]: value } : r)));
        setDirty(true);
    }, []);

    // Reemplaza el puesto de un row existente usando el path resuelto del árbol.
    const updateRowFromTree = useCallback(
        (localId: string, workPositionId: number, workPositionName: string) => {
            const path = findPositionPath(
                areas.filter((a) => a.area_level === 1),
                workPositionId,
            );
            setRows((prev) =>
                prev.map((r) => {
                    if (r.local_id !== localId) return r;
                    const subcompany = subcompanies.find((sc) => sc.subcompany_id === (path?.subcompany_id ?? r.subcompany_id));
                    return {
                        ...r,
                        work_position_id: workPositionId,
                        work_position_name: workPositionName,
                        area_ids: path?.area_ids ?? r.area_ids,
                        area_path_names: path?.area_path_names ?? r.area_path_names,
                        subcompany_id: path?.subcompany_id ?? r.subcompany_id,
                        subcompany_name: subcompany?.subcompany_name ?? r.subcompany_name,
                    };
                }),
            );
            setDirty(true);
        },
        [areas, subcompanies],
    );

    const hasUnsavedChanges = useMemo(() => dirty, [dirty]);

    const rowDateErrors = useMemo(() => {
        const contractStart = selectedHiring?.start_date ? dayjs(selectedHiring.start_date) : null;
        const contractEnd = selectedHiring?.end_date ? dayjs(selectedHiring.end_date) : null;
        return computeDateErrors(rows, contractStart, contractEnd);
    }, [selectedHiring, rows]);

    const isValid = useMemo(() => {
        if (selectedHiringId === null) return false;
        if (rows.length === 0) return false;
        if (Object.values(rowDateErrors).some((errs) => errs.length > 0)) return false;
        const activeRows = rows.filter((r) => !r.to_delete);
        return activeRows.every(
            (r) =>
                r.subcompany_id > 0 &&
                r.work_position_id > 0 &&
                r.area_ids.length > 0 &&
                r.start_date &&
                r.end_date &&
                r.start_date.isValid() &&
                r.end_date.isValid() &&
                !r.end_date.isBefore(r.start_date, 'day'),
        );
    }, [selectedHiringId, rows, rowDateErrors]);

    return {
        selectedHiringId,
        selectedHiring,
        rows,
        subcompanyTree,
        hasUnsavedChanges,
        isValid,
        rowDateErrors,
        preloadedByHiring,
        preloadedCountByHiring,
        preloadedTotalContracts,
        preloadedTotalPositions,
        setSelectedHiringId: selectHiring,
        addRowFromTree,
        removeRow,
        restoreRow,
        toggleEditRow,
        updateRow,
        updateRowFromTree,
        changeRowSubcompany,
        changeRowArea,
        changeRowWorkPosition,
        changeRowDate,
        reset,
    };
};
